import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart as LineChartIcon, TrendingUp, TrendingDown, Trophy } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-effect p-3 rounded-lg border border-white/10">
        <p className="text-sm text-gray-400">
          {new Date(payload[0].payload.date).toLocaleDateString()}
        </p>
        <p className="text-lg font-bold text-[#00E0FF] mono">
          ${payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  }
  return null;
};

export default function PortfolioPerformanceAnalytics({ analytics }) {
  if (!analytics || !analytics.history || analytics.history.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-8"
    >
      <Card className="glass-effect">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
            <LineChartIcon className="w-6 h-6 text-[#00E0FF]" />
            PERFORMANCE ANALYTICS
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-6">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={analytics.history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  stroke="#64748B"
                  tick={{ fill: '#64748B', fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#64748B" tick={{ fill: '#64748B', fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="value" stroke="#00E0FF" strokeWidth={3} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="glass-effect p-4 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-400">30-Day Return</span>
                <TrendingUp className={`w-4 h-4 ${analytics.total_return >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`} />
              </div>
              <div className={`text-2xl font-bold mono ${analytics.total_return >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                {analytics.total_return >= 0 ? '+' : ''}{analytics.total_return}%
              </div>
            </div>

            {analytics.best_performer && (
              <div className="glass-effect p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Best Performer</span>
                  <Trophy className="w-4 h-4 text-[#00FFA3]" />
                </div>
                <div className="text-lg font-bold text-white mono mb-1">{analytics.best_performer.symbol}</div>
                <div className="text-sm text-[#00FFA3] mono">+{analytics.best_performer.return}%</div>
              </div>
            )}

            {analytics.worst_performer && (
              <div className="glass-effect p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Worst Performer</span>
                  <TrendingDown className="w-4 h-4 text-[#FF3B30]" />
                </div>
                <div className="text-lg font-bold text-white mono mb-1">{analytics.worst_performer.symbol}</div>
                <div className={`text-sm mono ${analytics.worst_performer.return >= 0 ? 'text-[#00FFA3]' : 'text-[#FF3B30]'}`}>
                  {analytics.worst_performer.return >= 0 ? '+' : ''}{analytics.worst_performer.return}%
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
